import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FloorPlanItem, TableItem, BarItem, BuffetItem, DecorItem, DanceFloorItem } from '../types';

// Conversion factor for feet to pixels (1 foot = 10 pixels)
const FEET_TO_PIXELS = 10;

// Modern color scheme matching App.tsx
const COLORS = {
  primary: '#fbf2ed', // Header color
  secondary: '#d0d7c9', // Button color
  accent: '#d0d7c9', // Accent color (using same as buttons)
  light: '#f8f9fa', // Light background
  dark: '#555555', // Dark text
  lightText: '#555555', // Updated for readability on light backgrounds
  border: '#dee2e6',
  hover: '#c2c9bc', // Darker button color for hover states
  success: '#28a745',
  error: '#dc3545',
  gray: '#6c757d',
  lightGray: '#e9ecef',
};

interface PropertyPanelProps {
  selectedItem: FloorPlanItem | null;
  onItemChange: (updatedItem: FloorPlanItem) => void;
  onDeleteItem: (id: string) => void;
}

const PanelContainer = styled.div`
  width: 260px;
  background-color: ${COLORS.light};
  border-left: 1px solid ${COLORS.border};
  padding: 20px 16px;
  height: 100%;
  overflow-y: auto;
  box-shadow: -1px 0 5px rgba(0, 0, 0, 0.05);
`;

const PanelTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  color: ${COLORS.primary};
  padding-bottom: 12px;
  border-bottom: 2px solid ${COLORS.border};
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.dark};
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${COLORS.border};
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  color: #555555;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 2px rgba(40, 181, 181, 0.2);
  }

  &:disabled {
    background-color: ${COLORS.lightGray};
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${COLORS.border};
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  color: #555555;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23343a40' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;

  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 2px rgba(40, 181, 181, 0.2);
  }

  &:disabled {
    background-color: ${COLORS.lightGray};
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  padding: 8px 16px;
  background-color: ${COLORS.error};
  color: #555555;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-top: 20px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);

  &:hover {
    background-color: #c82333;
    box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
    transform: translateY(-1px);
    color: #555555;
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(220, 53, 69, 0.2);
  }
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${COLORS.gray};
  margin-top: 4px;
  display: block;
`;

const NoSelection = styled.div`
  color: ${COLORS.gray};
  font-style: italic;
  font-size: 14px;
  text-align: center;
  margin-top: 40px;
`;

const PropertyPanel: React.FC<PropertyPanelProps> = ({
  selectedItem,
  onItemChange,
  onDeleteItem
}) => {
  // Add state for temporary input values
  const [inputValues, setInputValues] = useState<{
    width: string;
    height: string;
  }>({
    width: '',
    height: ''
  });

  // Update input values when selectedItem changes
  useEffect(() => {
    if (selectedItem) {
      const isItemWithFeetDimensions = 
        selectedItem.type === 'stage' || 
        selectedItem.type === 'bar' || 
        selectedItem.type === 'buffet' || 
        selectedItem.type === 'danceFloor';
      
      setInputValues({
        width: isItemWithFeetDimensions 
          ? (selectedItem.size.width / FEET_TO_PIXELS).toFixed(1) 
          : selectedItem.size.width.toString(),
        height: isItemWithFeetDimensions 
          ? (selectedItem.size.height / FEET_TO_PIXELS).toFixed(1) 
          : selectedItem.size.height.toString()
      });
    }
  }, [selectedItem]);

  if (!selectedItem) {
    return (
      <PanelContainer>
        <PanelTitle>Properties</PanelTitle>
        <NoSelection>No item selected</NoSelection>
      </PanelContainer>
    );
  }

  const handlePositionChange = (axis: 'x' | 'y', value: string) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      const updatedItem = {
        ...selectedItem,
        position: {
          ...selectedItem.position,
          [axis]: numValue
        }
      };
      onItemChange(updatedItem);
    }
  };

  const handleSizeInputChange = (dimension: 'width' | 'height', value: string) => {
    // Always update the input value state, even if empty
    setInputValues(prev => ({
      ...prev,
      [dimension]: value
    }));
  };

  const handleSizeKeyDown = (dimension: 'width' | 'height', e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only process changes on Enter key
    if (e.key === 'Enter') {
      const value = inputValues[dimension];
      
      // If input is empty or invalid, revert to the original value
      if (value.trim() === '' || isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
        const isItemWithFeetDimensions = 
          selectedItem.type === 'stage' || 
          selectedItem.type === 'bar' || 
          selectedItem.type === 'buffet' || 
          selectedItem.type === 'danceFloor';
        
        // Revert to original value
        setInputValues(prev => ({
          ...prev,
          [dimension]: isItemWithFeetDimensions 
            ? (selectedItem.size[dimension] / FEET_TO_PIXELS).toFixed(1) 
            : selectedItem.size[dimension].toString()
        }));
      } else {
        // Valid value, update the item
        handleSizeChange(dimension, value);
      }
    }
  };

  const handleSizeBlur = (dimension: 'width' | 'height') => {
    // Only revert on blur for items that don't use feet dimensions
    if (selectedItem.type !== 'stage' && 
        selectedItem.type !== 'bar' && 
        selectedItem.type !== 'buffet' &&
        selectedItem.type !== 'danceFloor') {
      const value = inputValues[dimension];
      
      // If input is empty or invalid, revert to the original value
      if (value.trim() === '' || isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
        // Revert to original value
        setInputValues(prev => ({
          ...prev,
          [dimension]: selectedItem.size[dimension].toString()
        }));
      }
    }
  };

  const handleSizeChange = (dimension: 'width' | 'height', value: string) => {
    const numValue = parseFloat(value);
    if (selectedItem.type === 'table' || selectedItem.type === 'chair') {
      return;
    }
    
    if (!isNaN(numValue) && numValue > 0) {
      // For stage, bar, buffet, and dance floor, convert from feet to pixels
      const isItemWithFeetDimensions = 
        selectedItem.type === 'stage' || 
        selectedItem.type === 'bar' || 
        selectedItem.type === 'buffet' || 
        selectedItem.type === 'danceFloor';
      
      const pixelValue = isItemWithFeetDimensions
        ? numValue * FEET_TO_PIXELS
        : numValue;
      
      const updatedItem = {
        ...selectedItem,
        size: {
          ...selectedItem.size,
          [dimension]: pixelValue
        }
      };
      onItemChange(updatedItem);
    }
  };

  const handleRotationChange = (value: string) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      const updatedItem = {
        ...selectedItem,
        rotation: numValue
      };
      onItemChange(updatedItem);
    }
  };

  const handleTableShapeChange = (shape: 'rectangle' | 'circle') => {
    return;
    
    // Keeping commented code for reference
    // if (selectedItem.type === 'table') {
    //   const tableItem = selectedItem as TableItem;
    //   const updatedItem = {
    //     ...tableItem,
    //     shape
    //   };
    //   onItemChange(updatedItem);
    // }
  };

  const handleSeatsChange = (value: string) => {
    return;
    
    // Keeping commented code for reference
    // const numValue = parseInt(value);
    // if (selectedItem.type === 'table' && !isNaN(numValue) && numValue > 0) {
    //   const tableItem = selectedItem as TableItem;
    //   const updatedItem = {
    //     ...tableItem,
    //     seats: numValue
    //   };
    //   onItemChange(updatedItem);
    // }
  };

  const handleLabelChange = (value: string) => {
    if (selectedItem.type === 'table') {
      const tableItem = selectedItem as TableItem;
      const updatedItem = {
        ...tableItem,
        label: value
      };
      onItemChange(updatedItem);
    } else if (selectedItem.type === 'bar') {
      const barItem = selectedItem as BarItem;
      const updatedItem = {
        ...barItem,
        label: value
      };
      onItemChange(updatedItem);
    } else if (selectedItem.type === 'buffet') {
      const buffetItem = selectedItem as BuffetItem;
      const updatedItem = {
        ...buffetItem,
        label: value
      };
      onItemChange(updatedItem);
    } else if (selectedItem.type === 'danceFloor') {
      const danceFloorItem = selectedItem as DanceFloorItem;
      const updatedItem = {
        ...danceFloorItem,
        label: value
      };
      onItemChange(updatedItem);
    }
  };

  const handleDecorTypeChange = (decorType: string) => {
    if (selectedItem.type === 'decor') {
      const decorItem = selectedItem as DecorItem;
      const updatedItem = {
        ...decorItem,
        decorType
      };
      onItemChange(updatedItem);
    }
  };

  const handleDelete = () => {
    onDeleteItem(selectedItem.id);
  };

  return (
    <PanelContainer>
      <PanelTitle>
        {selectedItem ? `Properties: ${selectedItem.type.charAt(0).toUpperCase() + selectedItem.type.slice(1)}` : 'Properties'}
      </PanelTitle>

      {selectedItem ? (
        <>
          <FormGroup>
            <Label>Position X</Label>
            <Input
              type="number"
              value={selectedItem.position.x}
              onChange={(e) => handlePositionChange('x', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Position Y</Label>
            <Input
              type="number"
              value={selectedItem.position.y}
              onChange={(e) => handlePositionChange('y', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              {(selectedItem.type === 'stage' || selectedItem.type === 'bar' || selectedItem.type === 'buffet' || selectedItem.type === 'danceFloor') ? 'Length (ft)' : 'Length (px)'}
            </Label>
            <Input
              type={selectedItem.type === 'stage' || selectedItem.type === 'bar' || selectedItem.type === 'buffet' || selectedItem.type === 'danceFloor' ? 'text' : 'number'}
              value={inputValues.width}
              onChange={(e) => handleSizeInputChange('width', e.target.value)}
              onKeyDown={(e) => handleSizeKeyDown('width', e)}
              onBlur={() => handleSizeBlur('width')}
              disabled={selectedItem.type === 'table' || selectedItem.type === 'chair'}
            />
            {(selectedItem.type === 'table' || selectedItem.type === 'chair') && (
              <HelperText>
                Length is locked for {selectedItem.type}s
              </HelperText>
            )}
            {(selectedItem.type === 'stage' || selectedItem.type === 'bar' || selectedItem.type === 'buffet' || selectedItem.type === 'danceFloor') && (
              <HelperText>
                Press Enter to apply changes
              </HelperText>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              {(selectedItem.type === 'stage' || selectedItem.type === 'bar' || selectedItem.type === 'buffet' || selectedItem.type === 'danceFloor') ? 'Width (ft)' : 'Width (px)'}
            </Label>
            <Input
              type={selectedItem.type === 'stage' || selectedItem.type === 'bar' || selectedItem.type === 'buffet' || selectedItem.type === 'danceFloor' ? 'text' : 'number'}
              value={inputValues.height}
              onChange={(e) => handleSizeInputChange('height', e.target.value)}
              onKeyDown={(e) => handleSizeKeyDown('height', e)}
              onBlur={() => handleSizeBlur('height')}
              disabled={selectedItem.type === 'table' || selectedItem.type === 'chair'}
            />
            {(selectedItem.type === 'table' || selectedItem.type === 'chair') && (
              <HelperText>
                Width is locked for {selectedItem.type}s
              </HelperText>
            )}
            {(selectedItem.type === 'stage' || selectedItem.type === 'bar' || selectedItem.type === 'buffet' || selectedItem.type === 'danceFloor') && (
              <HelperText>
                Press Enter to apply changes
              </HelperText>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Rotation (degrees)</Label>
            <Input
              type="number"
              value={selectedItem.rotation}
              onChange={(e) => handleRotationChange(e.target.value)}
            />
          </FormGroup>

          {selectedItem.type === 'table' && (
            <>
              <FormGroup>
                <Label>Shape</Label>
                <Select
                  value={(selectedItem as TableItem).shape}
                  onChange={(e) => handleTableShapeChange(e.target.value as 'rectangle' | 'circle')}
                  disabled
                >
                  <option value="rectangle">Rectangle</option>
                  <option value="circle">Circle</option>
                </Select>
                <HelperText>
                  Shape is locked for tables
                </HelperText>
              </FormGroup>

              <FormGroup>
                <Label>Seats</Label>
                <Input
                  type="number"
                  value={(selectedItem as TableItem).seats}
                  onChange={(e) => handleSeatsChange(e.target.value)}
                  disabled
                />
                <HelperText>
                  Number of seats is locked for tables
                </HelperText>
              </FormGroup>

              <FormGroup>
                <Label>Label</Label>
                <Input
                  type="text"
                  value={(selectedItem as TableItem).label || ''}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  placeholder="Table name or number"
                />
              </FormGroup>
            </>
          )}

          {selectedItem.type === 'bar' && (
            <>
              <FormGroup>
                <Label>Label</Label>
                <Input
                  type="text"
                  value={(selectedItem as BarItem).label || ''}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  placeholder="Bar name"
                />
              </FormGroup>
            </>
          )}

          {selectedItem.type === 'buffet' && (
            <>
              <FormGroup>
                <Label>Label</Label>
                <Input
                  type="text"
                  value={(selectedItem as BuffetItem).label || ''}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  placeholder="Buffet name"
                />
              </FormGroup>
            </>
          )}

          {selectedItem.type === 'danceFloor' && (
            <>
              <FormGroup>
                <Label>Label</Label>
                <Input
                  type="text"
                  value={(selectedItem as DanceFloorItem).label || ''}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  placeholder="Dance floor name"
                />
              </FormGroup>
            </>
          )}

          {selectedItem.type === 'decor' && (
            <FormGroup>
              <Label>Decor Type</Label>
              <Select
                value={(selectedItem as DecorItem).decorType}
                onChange={(e) => handleDecorTypeChange(e.target.value)}
              >
                <option value="flower">Flower</option>
                <option value="plant">Plant</option>
                <option value="candle">Candle</option>
                <option value="star">Star</option>
              </Select>
            </FormGroup>
          )}

          <DeleteButton onClick={handleDelete}>Delete Item</DeleteButton>
        </>
      ) : (
        <NoSelection>No item selected</NoSelection>
      )}
    </PanelContainer>
  );
};

export default PropertyPanel; 